// tv/tv-menu.jsx — inline TV-native d-pad disclosure for TVShell.
//
// Toggle lives in the parent's top chrome row (a ChromeBtn with "TV"
// + chevron); this component renders only the panel itself when
// `open`. Layout: Home / Menu / Back / Apple-TV row, then 3×3 d-pad
// with OK center, with the brand logo bottom-right.
//
// `onAction(verb)` receives one of: home | menu | back | up | down |
// left | right | ok. Mapping to the right TV-side service + vocabulary
// is handled by tv/remote-config.js (which the caller wires up).
// `onReturnToAppleTV` is optional — only rendered when the room has
// an Apple TV plus a `_watch` script; tapping fires script.<room>_watch.

import * as React from 'react';
import { AppleTvMark } from '../shared.jsx';
import { A_FONT, fs } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { BrandBadge } from './primitives.jsx';

// Sits inside the remote card as a quiet inset (cardSoft, no outline);
// its keys are raised card-coloured keys on that fill, OK takes the
// primary fill. `compact` (phone) moves the brand logo into flow under
// the d-pad, where the corner position would overlap the pad.
export function TVMenuDisclosure({ T, open, onAction, tvBrand, appleTv, onReturnToAppleTV, compact = false }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'relative',
      background: T.cardSoft, borderRadius: RADIUS.control,
      padding: compact ? '16px 12px' : '18px 20px',
      display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center',
      animation: 'a-pop .18s ease',
    }}>
      {/* Top row — Home / Menu / Back / Apple TV. Apple TV fires
          script.<room>_watch (ha-av's canonical wake recipe; works via
          HDMI-CEC on both Sony and LG). Only rendered when the room
          has an Apple TV mapped + a Watch script (room.av). */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
        <TVMenuBtn T={T} label="Home"   onClick={() => onAction('home')}/>
        <TVMenuBtn T={T} label="Menu"   onClick={() => onAction('menu')}/>
        <TVMenuBtn T={T} label="Back"   onClick={() => onAction('back')}/>
        {appleTv && onReturnToAppleTV && (
          <TVMenuBtn T={T} ariaLabel="Switch to Apple TV"
            onClick={onReturnToAppleTV}>
            <div style={{ width: 32, height: 32, display: 'flex' }}>
              <AppleTvMark radius={4} style={{ boxShadow: 'none' }}/>
            </div>
          </TVMenuBtn>
        )}
      </div>
      {/* 3×3 d-pad with OK center — centered horizontally so the
          joystick reads as one cohesive control. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 56px)',
        gridTemplateRows: 'repeat(3, 56px)',
        gap: 6,
      }}>
        <div/>
        <TVMenuBtn T={T} onClick={() => onAction('up')}    ariaLabel="Up">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 15l6-6 6 6"/></svg>
        </TVMenuBtn>
        <div/>
        <TVMenuBtn T={T} onClick={() => onAction('left')}  ariaLabel="Left">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
        </TVMenuBtn>
        <TVMenuBtn T={T} label="OK" accent onClick={() => onAction('ok')}/>
        <TVMenuBtn T={T} onClick={() => onAction('right')} ariaLabel="Right">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
        </TVMenuBtn>
        <div/>
        <TVMenuBtn T={T} onClick={() => onAction('down')}  ariaLabel="Down">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </TVMenuBtn>
        <div/>
      </div>
      {/* Brand logo bottom-right, absolute-positioned so the d-pad
          above stays cleanly centered. On a phone the corner overlaps
          the pad, so it drops into flow, right-aligned under it. */}
      {tvBrand && (
        <div style={compact ? {
          alignSelf: 'flex-end', display: 'flex', alignItems: 'center',
        } : {
          position: 'absolute', right: 20, bottom: 18,
          display: 'flex', alignItems: 'center', height: 56,
        }}>
          <BrandBadge T={T} brand={tvBrand} size="large"/>
        </div>
      )}
    </div>
  );
}

// Glyph keys and OK are 56px circles; worded keys (Home / Menu / Back)
// are 48px-tall rounded keys sized to their word. Keys are the card
// colour raised off the inset's quiet fill; OK is the primary action.
function TVMenuBtn({ T, label, children, onClick, ariaLabel, accent = false }) {
  const worded = !children && !accent;
  return (
    <button onClick={onClick} aria-label={ariaLabel || label}
      style={{
        width: worded ? 'auto' : 56, minWidth: worded ? 68 : undefined,
        height: worded ? 48 : 56,
        padding: worded ? '0 14px' : 0,
        borderRadius: worded ? RADIUS.control : '50%',
        border: 'none',
        background: accent ? T.primary : T.card,
        color: accent ? T.primaryInk : T.ink,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: accent ? 'none' : T.keyShadow,
        fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        transition: 'background .12s',
      }}>
      {children || label}
    </button>
  );
}
